import Phaser from 'phaser';
import { ANIMAL_STATS, ALL_ANIMAL_KEYS } from '../config/animals';
import { LEVELS, MAX_LEVEL } from '../config/levels';
import { DEFAULT_CHARACTER } from '../config/presets';
import { makeMountWithRider } from '../render/mount';
import type { AnimalKey } from '../types';

// Pick animals for both players + which level to race on, then start
// the split-screen race in TwoPlayerRaceScene.

export class TwoPlayerSetupScene extends Phaser.Scene {
  private p1Idx = 0;
  private p2Idx = 1;
  private levelIdx = 1;
  private p1Display!: Phaser.GameObjects.Container;
  private p2Display!: Phaser.GameObjects.Container;
  private p1Name!: Phaser.GameObjects.Text;
  private p2Name!: Phaser.GameObjects.Text;
  private levelText!: Phaser.GameObjects.Text;

  constructor() {
    super('TwoPlayerSetupScene');
  }

  create(): void {
    const W = this.scale.width;
    const H = this.scale.height;
    this.add.rectangle(0, 0, W, H, 0xfceabb).setOrigin(0, 0);
    this.add
      .text(W / 2, 50, '2-Player Battle', {
        fontFamily: 'sans-serif',
        fontSize: '40px',
        color: '#3d2914',
      })
      .setOrigin(0.5);
    this.add
      .text(W / 2, 88, 'Each player picks an animal. First to the fruit target wins.', {
        fontFamily: 'sans-serif',
        fontSize: '16px',
        color: '#3d2914',
      })
      .setOrigin(0.5);

    // Two animal picker columns
    this.makePicker(W * 0.27, 'Player 1', 'p1');
    this.makePicker(W * 0.73, 'Player 2', 'p2');

    // Level picker
    this.add
      .text(W / 2, H - 165, 'Level:', {
        fontFamily: 'sans-serif',
        fontSize: '18px',
        color: '#3d2914',
      })
      .setOrigin(0.5);
    this.levelText = this.add
      .text(W / 2, H - 130, '', {
        fontFamily: 'sans-serif',
        fontSize: '22px',
        color: '#3d2914',
      })
      .setOrigin(0.5);
    this.makeArrow(W / 2 - 140, H - 116, '◀', () => {
      this.levelIdx = Math.max(1, this.levelIdx - 1);
      this.refreshLevel();
    });
    this.makeArrow(W / 2 + 140, H - 116, '▶', () => {
      this.levelIdx = Math.min(MAX_LEVEL, this.levelIdx + 1);
      this.refreshLevel();
    });
    this.refreshLevel();

    // Start + Back buttons
    const start = this.add.container(W / 2 + 80, H - 60);
    const startBg = this.add.rectangle(0, 0, 200, 50, 0x6b3aa8).setStrokeStyle(2, 0xffffff);
    const startTxt = this.add
      .text(0, 0, 'Start the Battle!', { fontFamily: 'sans-serif', fontSize: '20px', color: '#fff' })
      .setOrigin(0.5);
    start.add([startBg, startTxt]);
    start.setSize(200, 50).setInteractive({ useHandCursor: true });
    start.on('pointerover', () => startBg.setFillStyle(0x8a4ec2));
    start.on('pointerout', () => startBg.setFillStyle(0x6b3aa8));
    start.on('pointerdown', () => {
      const isBoss = LEVELS[this.levelIdx].hasBossAtEnd;
      if (isBoss) {
        this.scene.start('TwoPlayerBossScene', {
          animalP1: ALL_ANIMAL_KEYS[this.p1Idx],
          animalP2: ALL_ANIMAL_KEYS[this.p2Idx],
        });
      } else {
        this.scene.start('TwoPlayerRaceScene', {
          level: this.levelIdx,
          animalP1: ALL_ANIMAL_KEYS[this.p1Idx],
          animalP2: ALL_ANIMAL_KEYS[this.p2Idx],
        });
      }
    });

    const back = this.add.container(W / 2 - 140, H - 60);
    const backBg = this.add.rectangle(0, 0, 130, 50, 0x3d2914).setStrokeStyle(2, 0xffffff);
    const backTxt = this.add
      .text(0, 0, '← Back', { fontFamily: 'sans-serif', fontSize: '18px', color: '#fff' })
      .setOrigin(0.5);
    back.add([backBg, backTxt]);
    back.setSize(130, 50).setInteractive({ useHandCursor: true });
    back.on('pointerover', () => backBg.setFillStyle(0x6b3410));
    back.on('pointerout', () => backBg.setFillStyle(0x3d2914));
    back.on('pointerdown', () => this.scene.start('TitleScene'));
  }

  private makePicker(cx: number, label: string, key: 'p1' | 'p2'): void {
    this.add
      .text(cx, 130, label, {
        fontFamily: 'sans-serif',
        fontSize: '24px',
        color: '#3d2914',
      })
      .setOrigin(0.5);
    const display = this.add.container(cx, 280);
    if (key === 'p1') this.p1Display = display;
    else this.p2Display = display;

    const nameText = this.add
      .text(cx, 360, '', {
        fontFamily: 'sans-serif',
        fontSize: '22px',
        color: '#3d2914',
      })
      .setOrigin(0.5);
    if (key === 'p1') this.p1Name = nameText;
    else this.p2Name = nameText;

    this.makeArrow(cx - 130, 280, '◀', () => this.cycleAnimal(key, -1));
    this.makeArrow(cx + 130, 280, '▶', () => this.cycleAnimal(key, +1));

    this.refreshAnimal(key);

    // Player input cheatsheet
    const controls =
      key === 'p1' ? 'Controls: W = jump, S = duck' : 'Controls: ↑ = jump, ↓ = duck';
    this.add
      .text(cx, 400, controls, {
        fontFamily: 'sans-serif',
        fontSize: '14px',
        color: '#6b3410',
      })
      .setOrigin(0.5);
  }

  private cycleAnimal(key: 'p1' | 'p2', direction: 1 | -1): void {
    if (key === 'p1') {
      this.p1Idx = (this.p1Idx + direction + ALL_ANIMAL_KEYS.length) % ALL_ANIMAL_KEYS.length;
    } else {
      this.p2Idx = (this.p2Idx + direction + ALL_ANIMAL_KEYS.length) % ALL_ANIMAL_KEYS.length;
    }
    this.refreshAnimal(key);
  }

  private refreshAnimal(key: 'p1' | 'p2'): void {
    const display = key === 'p1' ? this.p1Display : this.p2Display;
    const name = key === 'p1' ? this.p1Name : this.p2Name;
    const idx = key === 'p1' ? this.p1Idx : this.p2Idx;
    display.removeAll(true);
    const animalKey: AnimalKey = ALL_ANIMAL_KEYS[idx];
    const m = makeMountWithRider(this, animalKey, DEFAULT_CHARACTER, 0, 0, {
      showRider: false,
    });
    m.setScale(1.6);
    display.add(m);
    name.setText(ANIMAL_STATS[animalKey].displayName);
  }

  private refreshLevel(): void {
    const cfg = LEVELS[this.levelIdx];
    const themeName = cfg.theme === 'river' ? 'ocean' : cfg.theme;
    this.levelText.setText(`${this.levelIdx} — ${themeName}`);
  }

  private makeArrow(x: number, y: number, glyph: string, onClick: () => void): void {
    const t = this.add
      .text(x, y, glyph, {
        fontFamily: 'sans-serif',
        fontSize: '32px',
        color: '#3d2914',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    t.on('pointerdown', onClick);
  }
}
